import { CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { Tag } from "antd";
import { useGetStructureStepLogQuery } from "api/TenantSlug/StepLog/hooks";
import { StepDetailRenderer } from "components/CC/RunHistory/StepDetails/StepDetailRenderer";
import { Drawer } from "components/UI/Drawer";
import { DrawerCollapsible } from "components/UI/DrawerCollapsible/DrawerCollapsible";
import { DrawerContent } from "components/UI/DrawerContent/DrawerContent";
import { DrawerTitle } from "components/UI/DrawerTitle/DrawerTitle";
import { PropertiesList } from "components/UI/PropertiesList/PropertiesList";
import { QueryState } from "components/UI/QueryState/QueryState";
import { useFormFieldTypeLabel } from "components/UI/Selects/FormFieldTypeSelectSearchable/useFormFieldTypeLabel";
import { useStepTypeLabel } from "components/UI/Selects/StepTypeSelectSearchable/useStepTypeLabel";
import { ShortenedIdWithCopy } from "components/UI/ShortenedIdWithCopy";
import { ApiResponseBodyCell } from "components/UI/Table/UI/ApiResponseBodyCell/ApiResponseBodyCell";
import { DateTimeCell } from "components/UI/Table/UI/DateTimeCell/DateTimeCell";
import { EmptyCell } from "components/UI/Table/UI/EmptyCell/EmptyCell";
import { EntityLinkCell } from "components/UI/Table/UI/EntityLinkCell/EntityLinkCell";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { FC } from "react";
import { DrawerId } from "types/models";

import StepLogStructureData = App.Data.StepLogStructureData;

interface StructureStepLogDetailDrawerProps {
    logId: string;
}

export const StructureStepLogDetailDrawer: FC<StructureStepLogDetailDrawerProps> = ({ logId }) => {
    const { t } = usePaldockTranslation("drawers.structure-step-log-detail");
    const { t: tG } = usePaldockTranslation("tables.general-columns");
    const { t: tGL } = usePaldockTranslation("tables.general-labels");
    const { t: tT } = usePaldockTranslation("tables.structure-step-log");
    const getStepTypeLabel = useStepTypeLabel();
    const getFormFieldTypeLabel = useFormFieldTypeLabel();
    const drawerId = DrawerId.StructureStepLogDetail;

    const getStructureStepLogQuery = useGetStructureStepLogQuery(logId, {
        filter: {
            include:
                "formField,formField.formStructure,conversionState,owner,offer,stepRun,stepRun.scenarioRun,stepRun.scenarioRun.scenario",
        },
    });
    const log = getStructureStepLogQuery.data?.structureLog;

    const logList = log
        ? PropertiesList.create<StepLogStructureData>(log, [
              {
                  term: tT("columns.started"),
                  description: ({ stepRun }) =>
                      stepRun?.started_at ? <DateTimeCell value={stepRun.started_at} /> : <EmptyCell />,
              },
              {
                  term: tG("created_at"),
                  description: ({ created_at }) => (created_at ? <DateTimeCell value={created_at} /> : <EmptyCell />),
              },
              {
                  term: tG("conversion_id"),
                  description: ({ origin_eid }) =>
                      origin_eid ? <ShortenedIdWithCopy charactersBetweenDots={35} id={origin_eid} /> : <EmptyCell />,
              },
              {
                  term: tG("event_id"),
                  description: ({ eid }) =>
                      eid ? <ShortenedIdWithCopy charactersBetweenDots={35} id={eid} /> : <EmptyCell />,
              },
              {
                  term: tT("columns.structure"),
                  description: ({ formField }) => <EntityLinkCell.FormStructure entity={formField?.formStructure} />,
              },
              {
                  term: tT("columns.form_field"),
                  description: ({ formField }) => formField?.system_name ?? <EmptyCell />,
              },
              {
                  term: tT("columns.type"),
                  description: ({ formField }) =>
                      formField?.type ? getFormFieldTypeLabel(formField.type) : <EmptyCell />,
              },
              {
                  term: tT("columns.step"),
                  description: ({ stepRun }) => stepRun?.title ?? <EmptyCell />,
              },
              {
                  term: tT("columns.type"),
                  description: ({ stepRun }) => (stepRun?.type ? getStepTypeLabel(stepRun.type) : <EmptyCell />),
              },
              {
                  term: tG("status"),
                  description: ({ http_status_code }) => http_status_code ?? <EmptyCell />,
              },
              {
                  term: tG("name"),
                  description: ({ stepRun }) => <EntityLinkCell.Scenario entity={stepRun?.scenarioRun?.scenario} />,
              },
              {
                  term: tG("state"),
                  description: ({ state }) =>
                      state ? (
                          <Tag icon={<CheckCircleOutlined />} color="green">
                              {tGL("ok")}
                          </Tag>
                      ) : (
                          <Tag icon={<CloseCircleOutlined />} color="red">
                              {tGL("fail")}
                          </Tag>
                      ),
              },
              {
                  term: tT("columns.detail"),
                  description: ({ message, id }) =>
                      message != null ? (
                          <ApiResponseBodyCell response={{ body: message }} id={`message-${id}`} lines={10} />
                      ) : (
                          <EmptyCell />
                      ),
              },
          ])
        : null;

    return (
        <Drawer
            drawerId={drawerId}
            title={<DrawerTitle mainTitle={t("title")} />}
            searchParamsToClean={[`${drawerId}-id`]}
        >
            {!log ? (
                <QueryState
                    isLoading={getStructureStepLogQuery.isLoading}
                    isError={getStructureStepLogQuery.isError || (!getStructureStepLogQuery.isLoading && !log)}
                    errorKey="errors.something_went_wrong"
                />
            ) : (
                <div className="flex flex-col gap-4">
                    <DrawerContent className="flex flex-col gap-4 p-4">
                        <DrawerCollapsible title={t("sections.log")}>
                            <PropertiesList list={logList ?? []} />
                        </DrawerCollapsible>
                        {log.stepRun && (
                            <StepDetailRenderer
                                stepRun={log.stepRun}
                                logContext={{
                                    httpStatusCode: log.http_status_code,
                                    message: log.message,
                                    conversionState: log.conversionState,
                                }}
                            />
                        )}
                    </DrawerContent>
                </div>
            )}
        </Drawer>
    );
};
